/**
 * KPIDetailPanel — Slide-out panel showing full KPI details.
 *
 * Includes: progress chart, check-in timeline, comments, reviews,
 * AI insights, and actions (check-in, review).
 *
 * Uses a custom layout (not SlidePanel) because of the complex
 * progress hero header with chart.
 */
import React, { useEffect, useState } from 'react';
import X from 'lucide-react/dist/esm/icons/x';
import Target from 'lucide-react/dist/esm/icons/target';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import MessageSquare from 'lucide-react/dist/esm/icons/message-square';
import ClipboardCheck from 'lucide-react/dist/esm/icons/clipboard-check';
import Send from 'lucide-react/dist/esm/icons/send';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import CalendarDays from 'lucide-react/dist/esm/icons/calendar-days';
import Archive from 'lucide-react/dist/esm/icons/archive';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import Download from 'lucide-react/dist/esm/icons/download';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign';
import { Button, Spinner, IconButton, Badge } from '../../../components/ui';
import type { KPIAssignment, KPICheckIn } from '../../../services/api/kpi';
import * as kpiApi from '../../../services/api/kpi';
import { useKPIStore } from '../../../stores/kpiStore';
import { useOrgStore } from '../../../stores/orgStore';
import KPIStatusBadge from './KPIStatusBadge';
import KPIProgressChart from './KPIProgressChart';
import CheckInForm from './CheckInForm';

const CADENCE_DAYS: Record<string, number> = {
  weekly: 7, biweekly: 14, monthly: 30, quarterly: 91,
};
const LATE_THRESHOLD_DAYS = 3;

interface CheckInSlot {
  dueDate: Date;
  status: 'completed' | 'late' | 'current' | 'upcoming';
}

function computeCheckInSlots(
  cycleStart: string | undefined,
  cycleEnd: string | undefined,
  cadence: string | undefined,
  checkInCount: number,
): CheckInSlot[] {
  if (!cycleStart || !cycleEnd || !cadence) return [];
  const interval = CADENCE_DAYS[cadence];
  if (!interval) return [];
  const start = new Date(cycleStart + 'T00:00:00');
  const end = new Date(cycleEnd + 'T23:59:59');
  const now = new Date();
  const slots: CheckInSlot[] = [];
  const cur = new Date(start);
  cur.setDate(cur.getDate() + interval);
  while (cur <= end) {
    slots.push({ dueDate: new Date(cur), status: 'upcoming' });
    cur.setDate(cur.getDate() + interval);
  }
  if (slots.length === 0) slots.push({ dueDate: new Date(end), status: 'upcoming' });
  const completed = Math.min(checkInCount, slots.length);
  for (let i = 0; i < completed; i++) slots[i].status = 'completed';
  for (let i = completed; i < slots.length; i++) {
    const due = slots[i].dueDate;
    if (now > due) {
      const daysLate = Math.floor((now.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
      slots[i].status = daysLate >= LATE_THRESHOLD_DAYS ? 'late' : 'current';
    } else {
      if (i === completed) slots[i].status = 'current';
      break;
    }
  }
  return slots;
}

type DetailTab = 'progress' | 'comments' | 'review';

interface KPIDetailPanelProps {
  assignment: KPIAssignment | null;
  isOpen: boolean;
  onClose: () => void;
}

const KPIDetailPanel: React.FC<KPIDetailPanelProps> = ({ assignment, isOpen, onClose }) => {
  const {
    detailCheckIns, detailComments, detailReviews, detailLoading,
    loadAssignmentDetail, submitCheckIn, addComment,
    deleteAssignment, archiveAssignment,
    showCheckInForm: storeShowCheckIn, setShowCheckInForm,
  } = useKPIStore();
  const { userContext } = useOrgStore();
  const isOwnKPI = assignment?.assignee_id === userContext?.id;
  const isAdmin = userContext?.system_role !== 'user';

  const [activeTab, setActiveTab] = useState<DetailTab>('progress');
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [aiInsight, setAiInsight] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<'delete' | 'archive' | null>(null);

  useEffect(() => {
    if (isOpen && assignment) {
      loadAssignmentDetail(assignment.id);
      setActiveTab('progress');
      setShowCheckIn(storeShowCheckIn);
      setAiInsight(null);
      if (storeShowCheckIn) setShowCheckInForm(false);
    }
  }, [isOpen, assignment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAIInsight = async () => {
    if (!assignment) return;
    setAiLoading(true);
    try {
      const { aiAssignmentSummary } = await import('../../../services/api/kpi');
      const resp = await aiAssignmentSummary(assignment.id);
      setAiInsight(resp.summary);
    } catch {
      setAiInsight('Unable to generate insight.');
    }
    setAiLoading(false);
  };

  const handleSendComment = async () => {
    if (!commentText.trim() || !assignment) return;
    setSendingComment(true);
    try {
      await addComment(assignment.id, commentText.trim());
      setCommentText('');
    } finally {
      setSendingComment(false);
    }
  };

  if (!isOpen || !assignment) return null;

  const a = assignment;
  const pct = a.current_percentage ?? 0;

  const formatValue = (val: number | null) => {
    if (val === null || val === undefined) return '—';
    const label = a.definition_unit_label || '';
    if (a.definition_unit === 'percentage') return `${val}${label}`;
    if (a.definition_unit === 'currency') return `${label}${val.toLocaleString()}`;
    return `${val.toLocaleString()}${label ? ` ${label}` : ''}`;
  };

  return (
    <div className="fixed inset-0 z-modal flex justify-end" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="absolute inset-0 bg-surface-overlay backdrop-blur-sm animate-fade-in" onClick={onClose} />

      <div className="relative w-full max-w-[520px] bg-gradient-to-b from-surface-panel-from to-surface-panel-to border-l border-subtle shadow-2xl flex flex-col animate-slide-in-right overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-subtle">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-content">{a.definition_name}</h3>
              <p className="text-xs text-content-tertiary mt-0.5 capitalize">{a.definition_category?.replace('_', ' ')} · {a.definition_cadence}</p>
            </div>
            <IconButton icon={<X size={18} />} variant="ghost" aria-label="Close panel" onClick={onClose} />
          </div>

          {/* Progress Hero */}
          <div className="flex items-end gap-6">
            <div>
              <div className="text-3xl font-bold text-content tabular-nums">
                {a.current_percentage !== null ? `${Math.round(pct)}%` : '—'}
              </div>
              <div className="text-xs text-content-tertiary mt-0.5">
                {formatValue(a.current_value)} of {formatValue(a.target_value)}
              </div>
            </div>
            <KPIStatusBadge percentage={a.current_percentage} status={a.status} thresholds={a.definition_thresholds} size="md" />
          </div>

          {/* Financial Impact Summary */}
          {(a.financial_impact_target != null || a.financial_impact_actual != null) && (
            <div className="mt-3 flex items-center gap-3 px-3 py-2 rounded-lg bg-emerald-400/[0.06] border border-emerald-400/10">
              <DollarSign size={14} className="text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-content-secondary">
                  <span className="font-medium text-emerald-400 tabular-nums">
                    ${(a.financial_impact_actual ?? 0).toLocaleString()}
                  </span>
                  {a.financial_impact_target != null && (
                    <span className="text-content-muted">
                      {' '}of ${a.financial_impact_target.toLocaleString()} target
                    </span>
                  )}
                </div>
              </div>
              {a.financial_impact_target != null && a.financial_impact_target > 0 && (
                <span className="text-[11px] font-medium tabular-nums text-emerald-400">
                  {Math.round(((a.financial_impact_actual ?? 0) / a.financial_impact_target) * 100)}%
                </span>
              )}
            </div>
          )}

          {/* Check-in timeline bar */}
          {(() => {
            const slots = computeCheckInSlots(
              a.cycle_start_date, a.cycle_end_date,
              a.definition_cadence, a.check_in_count ?? detailCheckIns.length,
            );
            const completedCount = slots.filter(s => s.status === 'completed').length;
            const lateCount = slots.filter(s => s.status === 'late').length;
            if (slots.length === 0) return null;
            return (
              <div className="mt-3">
                <div className="flex gap-[2px] h-2.5 rounded-full overflow-hidden">
                  {slots.map((slot, i) => (
                    <div
                      key={i}
                      className={`flex-1 rounded-sm transition-colors
                        ${slot.status === 'completed' ? 'bg-blue-400/40' : ''}
                        ${slot.status === 'late' ? 'bg-status-error/50' : ''}
                        ${slot.status === 'current' ? 'bg-blue-400/25' : ''}
                        ${slot.status === 'upcoming' ? 'bg-surface-secondary' : ''}
                      `}
                      title={`${slot.dueDate.toLocaleDateString()} — ${slot.status}`}
                    />
                  ))}
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[11px] text-content-tertiary">
                    {a.check_in_count ?? detailCheckIns.length} check-in{(a.check_in_count ?? detailCheckIns.length) !== 1 ? 's' : ''}
                    {lateCount > 0 && <span className="text-status-error/80 ml-1">({lateCount} overdue)</span>}
                  </span>
                </div>
              </div>
            );
          })()}

          {/* Performance chart */}
          {detailCheckIns.length >= 2 && (
            <div className="mt-2">
              <KPIProgressChart
                checkIns={detailCheckIns.map(ci => ({
                  date: ci.submitted_at,
                  value: ci.value ?? 0,
                  percentage: ci.percentage,
                }))}
                targetValue={a.target_value}
                baselineValue={a.baseline_value}
                currentPercentage={a.current_percentage}
                unit={a.definition_unit}
                unitLabel={a.definition_unit_label}
                height={120}
                variant="full"
              />
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-6 py-2 border-b border-subtle">
          {([
            { id: 'progress' as const, label: 'Progress', icon: TrendingUp, count: detailCheckIns.length },
            { id: 'comments' as const, label: 'Comments', icon: MessageSquare, count: detailComments.length },
            { id: 'review' as const, label: 'Reviews', icon: ClipboardCheck, count: detailReviews.length },
          ]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer border-none outline-none
                ${activeTab === tab.id ? 'bg-status-success/10 text-status-success' : 'text-content-muted hover:text-content-secondary hover:bg-surface-primary'}
              `}
            >
              <tab.icon size={12} />
              {tab.label}
              {tab.count > 0 && <span className="text-[10px] text-content-muted ml-0.5">{tab.count}</span>}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0 space-y-4">
          {detailLoading && (
            <div className="flex items-center justify-center py-12">
              <Spinner variant="success" />
            </div>
          )}

          {!detailLoading && activeTab === 'progress' && (
            <>
              {/* Actions */}
              <div className="flex items-center gap-2">
                {isOwnKPI && (
                  <button
                    onClick={() => setShowCheckIn(!showCheckIn)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-status-success bg-status-success/10 hover:bg-status-success/15 transition-colors cursor-pointer border border-status-success/20 outline-none"
                  >
                    <Send size={12} />
                    Check In
                  </button>
                )}
                {!isOwnKPI && a.assignee_name && (
                  <span className="text-xs text-content-tertiary italic">
                    Assigned to {a.assignee_name}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadAIInsight}
                  loading={aiLoading}
                  leftIcon={!aiLoading ? <Sparkles size={12} className="text-purple-400" /> : undefined}
                  className="text-purple-400 bg-purple-400/10 hover:bg-purple-400/15 border border-purple-400/15"
                >
                  AI Insight
                </Button>
              </div>

              {/* Check-in form */}
              {showCheckIn && (
                <CheckInForm
                  assignment={a}
                  onSubmit={submitCheckIn}
                  onClose={() => setShowCheckIn(false)}
                />
              )}

              {/* AI Insight */}
              {aiInsight && (
                <div className="p-3 rounded-xl bg-purple-400/[0.04] border border-purple-400/15 animate-fade-in">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Sparkles size={12} className="text-purple-400" />
                    <span className="text-[11px] font-medium text-purple-400">AI Insight</span>
                  </div>
                  <div className="text-xs text-content-secondary leading-relaxed whitespace-pre-wrap">{aiInsight}</div>
                </div>
              )}

              {/* Check-in Timeline */}
              <div className="space-y-2">
                <h4 className="text-[11px] text-content-tertiary uppercase tracking-wider">Check-in History</h4>
                {detailCheckIns.length > 0 ? detailCheckIns.map(ci => (
                  <CheckInCard
                    key={ci.id}
                    checkIn={ci}
                    formatValue={formatValue}
                    isOwnKPI={a.assignee_id === userContext?.id}
                    canVerify={!!(userContext && a.assignee_id !== userContext.id && (
                      userContext.system_role !== 'user' ||
                      a.verifier_id === userContext.id ||
                      a.setter_id === userContext.id
                    ))}
                  />
                )) : (
                  <p className="text-xs text-content-muted py-6 text-center">No check-ins yet</p>
                )}
              </div>
            </>
          )}

          {!detailLoading && activeTab === 'comments' && (
            <div className="space-y-3">
              {detailComments.map(c => (
                <div key={c.id} className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-surface-secondary flex items-center justify-center text-[10px] font-bold text-content-secondary shrink-0">
                    {(c.author_name || '?')[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-content">{c.author_name || 'Unknown'}</span>
                      <span className="text-[11px] text-content-muted">{new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-xs text-content-tertiary mt-0.5">{c.text}</p>
                  </div>
                </div>
              ))}

              {detailComments.length === 0 && (
                <p className="text-xs text-content-muted py-6 text-center">No comments yet. Start the conversation.</p>
              )}

              {/* Comment input */}
              <div className="flex gap-2 pt-2 border-t border-subtle">
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendComment()}
                  placeholder="Add a comment..."
                  className="flex-1 px-3 py-2 rounded-xl bg-surface-primary border border-subtle text-content text-xs placeholder-content-muted outline-none focus:border-brand/30 transition-all"
                />
                <IconButton
                  icon={sendingComment ? <Spinner size="sm" variant="brand" /> : <Send size={14} />}
                  variant="primary"
                  size="md"
                  aria-label="Send comment"
                  onClick={handleSendComment}
                  disabled={!commentText.trim() || sendingComment}
                  className="bg-brand/10 text-primary hover:bg-brand/20"
                />
              </div>
            </div>
          )}

          {!detailLoading && activeTab === 'review' && (
            <div className="space-y-3">
              {detailReviews.map(r => (
                <div key={r.id} className="p-3 rounded-xl bg-surface-primary border border-subtle">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-content">
                      {r.reviewer_name || 'Unknown'} · <span className="text-content-muted capitalize">{r.review_type.replace('_', ' ')}</span>
                    </span>
                    {r.score && (
                      <span className="text-sm font-bold text-primary tabular-nums">{r.score}/5</span>
                    )}
                  </div>
                  {r.strengths && <p className="text-[11px] text-emerald-400/80 mb-1">Strengths: {r.strengths}</p>}
                  {r.improvements && <p className="text-[11px] text-amber-400/80 mb-1">Areas to improve: {r.improvements}</p>}
                  {r.comments && <p className="text-[11px] text-content-tertiary">{r.comments}</p>}
                </div>
              ))}

              {detailReviews.length === 0 && (
                <p className="text-xs text-content-muted py-6 text-center">No reviews yet. Reviews are submitted during the review phase.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer — Archive / Delete actions (admin or setter only) */}
        {isAdmin && assignment.status !== 'cancelled' && (
          <div className="px-6 py-3 border-t border-subtle flex items-center gap-2">
            {showDeleteConfirm ? (
              <div className="flex items-center gap-2 flex-1">
                <span className="text-xs text-status-error">
                  {showDeleteConfirm === 'delete' ? 'Delete this KPI and all its data?' : 'Archive this KPI?'}
                </span>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={async () => {
                    if (showDeleteConfirm === 'delete') {
                      await deleteAssignment(a.id);
                    } else {
                      await archiveAssignment(a.id);
                    }
                    onClose();
                  }}
                >
                  Yes, {showDeleteConfirm}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(null)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <IconButton
                  icon={<Archive size={14} />}
                  variant="ghost"
                  aria-label="Archive this KPI"
                  onClick={() => setShowDeleteConfirm('archive')}
                  className="hover:text-amber-400 hover:bg-amber-400/10"
                />
                <IconButton
                  icon={<Trash2 size={14} />}
                  variant="danger"
                  aria-label="Delete this KPI"
                  onClick={() => setShowDeleteConfirm('delete')}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  submitted: { label: 'Submitted', color: 'text-content-muted bg-surface-secondary' },
  verified: { label: 'Verified', color: 'text-status-success bg-status-success/10' },
  needs_revision: { label: 'Needs Revision', color: 'text-status-warning bg-status-warning/10' },
};

const CheckInCard: React.FC<{
  checkIn: KPICheckIn;
  formatValue: (v: number | null) => string;
  isOwnKPI: boolean;
  canVerify: boolean;
}> = ({ checkIn: ci, formatValue, isOwnKPI, canVerify }) => {
  const { verifyCheckIn } = useKPIStore();
  const [verifying, setVerifying] = useState(false);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [showRevisionInput, setShowRevisionInput] = useState(false);

  const statusCfg = STATUS_CONFIG[ci.status] || STATUS_CONFIG.submitted;

  const handleVerify = async (status: string, notes?: string) => {
    setVerifying(true);
    try {
      await verifyCheckIn(ci.id, status, notes);
      setShowRevisionInput(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleDownload = async (evidenceId: string) => {
    try {
      const { download_url } = await kpiApi.getEvidenceDownloadUrl(evidenceId);
      window.open(download_url, '_blank');
    } catch { /* ignore */ }
  };

  return (
    <div className="p-3 rounded-xl bg-surface-primary border border-subtle space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-content">
          {formatValue(ci.value)}
          {ci.percentage !== null && (
            <span className="text-content-tertiary ml-1.5">({Math.round(ci.percentage)}%)</span>
          )}
        </span>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium ${statusCfg.color}`}>
            {statusCfg.label}
          </span>
          <span className="text-[11px] text-content-muted">
            {new Date(ci.submitted_at).toLocaleDateString()}
          </span>
        </div>
      </div>

      {ci.financial_impact_value !== null && ci.financial_impact_value !== undefined && (
        <p className="text-[11px] text-status-success">Financial impact: ${ci.financial_impact_value.toLocaleString()}</p>
      )}

      {ci.notes && <p className="text-[11px] text-content-tertiary">{ci.notes}</p>}
      {ci.submitter_name && (
        <p className="text-[11px] text-content-muted">by {ci.submitter_name}</p>
      )}

      {ci.reviewer_notes && (
        <p className="text-[11px] text-status-warning/80 italic">
          Review note: {ci.reviewer_notes}
          {ci.reviewer_name && <span className="text-content-muted"> — {ci.reviewer_name}</span>}
        </p>
      )}

      {/* Evidence files */}
      {ci.evidence && ci.evidence.length > 0 && (
        <div className="space-y-1 pt-1">
          {ci.evidence.map(ev => (
            <button
              key={ev.id}
              onClick={() => handleDownload(ev.id)}
              className="flex items-center gap-2 px-2 py-1 rounded-lg bg-surface-secondary/50 hover:bg-surface-secondary text-xs text-content-secondary w-full text-left border-none outline-none cursor-pointer transition-colors"
            >
              <FileText size={12} className="text-content-muted shrink-0" />
              <span className="flex-1 truncate">{ev.original_filename}</span>
              <Download size={10} className="text-content-muted shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Verify / Reject actions */}
      {canVerify && ci.status === 'submitted' && (
        <div className="pt-1 space-y-2">
          {showRevisionInput ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={revisionNotes}
                onChange={(e) => setRevisionNotes(e.target.value)}
                placeholder="Revision feedback..."
                className="flex-1 px-2 py-1.5 rounded-lg bg-surface-secondary border border-subtle text-content text-xs outline-none"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRevisionInput(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleVerify('needs_revision', revisionNotes)}
                loading={verifying}
              >
                Send
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="success"
                size="sm"
                onClick={() => handleVerify('verified')}
                loading={verifying}
                leftIcon={<CheckCircle size={12} />}
              >
                Verify
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRevisionInput(true)}
                leftIcon={<AlertTriangle size={12} />}
                className="text-status-warning"
              >
                Request Revision
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(KPIDetailPanel);
